import {defineSlotRecipe} from '@chakra-ui/react3'

export default defineSlotRecipe({
    className: 'leap-product-tile',
    slots: [
        'root',
        'container',
        'imagesContainer',
        'image',
        'unavailableContainer',
        'unavailableIconBox',
        'unavailableCloseIcon',
        'unavailableText',
        'badgesContainer',
        'productInfoContainer',
        'productDetailsContainer',
        'productName',
        'subline',
        'customizeContainer',
        'customizeText',
        'payLaterContainer',
        'price',
        'swatchesContainer',
        'swatch',
        'swatchMoreButton',
        'swatchMoreMobile',
        'swatchMoreDesktop',
        'swatchMoreMobileLabel',
        'swatchMoreDesktopLabel',
        'overlayActionContainer'
    ],
    base: {
        root: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12'
        },
        container: {
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-20',
            // change the visible elements when the element is hovered or focused
            '&:hover [data-not-hovered="container"], &:focus-within [data-not-hovered="container"]':
                {
                    opacity: 0
                },
            '&:not(:hover):not(:focus-within) [data-hovered="container"]': {
                opacity: 0
            }
        },
        imagesContainer: {
            position: 'relative',
            aspectRatio: '1/1',
            backgroundColor:
                'var(--module-surface-bg-color, {colors.background.surface.on-section-subtle})',
            borderRadius: 'm'
        },
        image: {
            position: 'absolute',
            inset: 0,
            objectFit: 'cover',
            borderRadius: 'm'
        },
        unavailableContainer: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'spacing-12',
            color: 'text.secondary',
            backgroundColor:
                'var(--module-surface-bg-color, {colors.background.surface.on-section-subtle})',
            borderRadius: 'm'
        },
        unavailableIconBox: {
            position: 'relative'
        },
        unavailableCloseIcon: {
            position: 'absolute',
            top: '-0.28rem',
            right: '-0.32rem',
            backgroundColor: 'text.secondary',
            display: 'flex',
            padding: 'spacing-3',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 'full'
        },
        unavailableText: {
            maxWidth: '11.25rem',
            textAlign: 'center',
            textStyle: 'body.small'
        },
        badgesContainer: {
            position: 'absolute',
            insetBlockStart: 'spacing-12',
            insetInlineStart: 'spacing-12',
            display: 'flex',
            flexDirection: 'row',
            gap: 'spacing-4'
        },
        productInfoContainer: {
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-12'
        },
        productDetailsContainer: {
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: 'spacing-6',
            textAlign: 'start'
        },
        productName: {
            alignSelf: 'stretch',
            color: 'text.primary',
            textStyle: 'body.medium'
        },
        subline: {
            alignSelf: 'stretch',
            color: 'text.secondary',
            textStyle: 'body.medium'
        },
        customizeContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 'spacing-14',
            _icon: {
                width: 'spacing-18',
                height: 'spacing-18',
                outline: '{borders.sm} {colors.border.brand}',
                outlineOffset: '{spacing.spacing-2}',
                borderRadius: 'full'
            }
        },
        payLaterContainer: {
            alignSelf: 'stretch',
            backgroundColor:
                'var(--module-surface-bg-color, {colors.background.surface.on-section-subtle})',
            borderRadius: 'm',
            padding: 'spacing-12',
            '& iframe': {
                display: 'block'
            },
            '& klarna-placement::part(osm-container)': {
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                flexDirection: {base: 'column', s: 'row'},
                alignItems: {base: 'flex-start', s: 'center'}
            },
            '& klarna-placement::part(osm-message)': {
                color: 'text.primary',
                marginLeft: {base: '-spacing-14', s: 0},
                marginTop: {base: 'spacing-12', s: 0}
            },
            '& klarna-placement::part(osm-legal)': {
                color: 'text.secondary',
                marginLeft: {base: '-spacing-14', s: 0}
            },
            '& klarna-placement::part(osm-cta)': {
                color: 'text.primary',
                display: {base: 'block', s: 'inline-block'},
                marginTop: {base: 'spacing-4', s: 0}
            }
        },
        customizeText: {
            color: 'text.secondary',
            textStyle: 'body.small'
        },
        price: {},
        swatchesContainer: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        swatch: {
            '&:nth-child(n+5)': {
                display: {base: 'none', s: 'flex'}
            },
            '&:nth-child(n+9)': {
                display: {s: 'none'}
            }
        },
        swatchMoreButton: {
            padding: 'spacing-3',
            margin: '-spacing-3',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'full',
            _focusVisible: {
                outline: '{borders.md} {colors.border.brand}',
                outlineOffset: '-2px'
            },
            '&>*': {
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 'spacing-20',
                height: 'spacing-20',
                paddingInline: 'spacing-6',
                borderRadius: 'full',
                backgroundColor: 'background.actions.ghost',
                backdropFilter: 'blur(4px)',
                color: 'text.secondary',
                textStyle: 'body.xsmall',
                textAlign: 'center'
            }
        },
        swatchMoreMobile: {
            display: {base: 'flex', s: 'none'}
        },
        swatchMoreDesktop: {
            display: 'flex'
        },
        swatchMoreMobileLabel: {
            display: {base: 'flex', s: 'none'}
        },
        swatchMoreDesktopLabel: {
            display: {base: 'none', s: 'flex'}
        },
        overlayActionContainer: {
            position: 'absolute',
            insetBlockStart: 'spacing-12',
            insetInlineEnd: 'spacing-12'
        }
    },
    variants: {
        background: {
            subtle: {
                root: {
                    '--module-surface-bg-color': 'colors.background.surface.on-section-subtle',
                    backgroundColor: 'background.section.subtle',
                    paddingBlockEnd: 'spacing-20',
                    borderRadius: 'm'
                },
                imagesContainer: {
                    backgroundColor: 'background.surface.on-section-default'
                },
                productInfoContainer: {
                    paddingInline: 'spacing-24'
                },
                swatchesContainer: {
                    paddingInline: 'spacing-24'
                }
            }
        },
        disabled: {
            true: {
                root: {
                    cursor: 'not-allowed'
                },
                container: {
                    cursor: 'inherit'
                }
            }
        },
        showBadges: {true: {}, false: {}},
        showCustomizable: {true: {}, false: {}},
        showPrice: {true: {}, false: {}},
        showVariations: {true: {}, false: {}}
    },
    defaultVariants: {}
})
